// ===========================================================
// CREATOR PUBLIC TYPES
//
// Clean, standardized frontend model.
// These types intentionally avoid backend-specific payload shape.
// ===========================================================

//------------------------------------------------------------ types.
export const CreatorFileImageCoverType = "image-cover";
export const CreatorFileImageTargetType = "image-target";
export const CreatorFileImageType = "image";
export const CreatorFileVideoType = "video";
export const CreatorFileVideoAlphaType = "video-alpha";
export const CreatorFileGlbType = "glb";

export type CreatorFileType =
  | typeof CreatorFileImageCoverType
  | typeof CreatorFileImageTargetType
  | typeof CreatorFileImageType
  | typeof CreatorFileVideoType
  | typeof CreatorFileVideoAlphaType
  | typeof CreatorFileGlbType;

export const CreatorArtworkImageTargetType = "image-target";
export const CreatorArtworkWorldTargetType = "world-target";
export const CreatorArtworkCollectionType = "collection";
export type CreatorArtworkType = typeof CreatorArtworkImageTargetType | typeof CreatorArtworkWorldTargetType;

//------------------------------------------------------------ files.
export interface CreatorFile {
  id?: string;
  type: CreatorFileType;
  path: string;
  width?: number;
  height?: number;
  size?: number;
}

//------------------------------------------------------------ scene graph.
export interface CreatorSceneAsset {
  assetID: string;
  assetType: string;
  assetPath: string;
}

export interface CreatorSceneChild {
  sceneID: string;
  assetID: string;
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
}

export interface CreatorSceneRoot {
  sceneID: string;
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
  children?: CreatorSceneChild[];
}

export interface CreatorSceneConfig {
  version: number;
  assets: CreatorSceneAsset[];
  scenes: CreatorSceneRoot[];
}

//------------------------------------------------------------ artwork / collection.
export interface CreatorArtworkBase {
  id: string;
  name?: string;
  description?: string;
  files?: CreatorFile[];
  /** Server-resolved cover image URL, stamped by the launch payload mint
   *  (image-cover → image-target → image precedence). Consumers display this
   *  value directly — the same value backs the page's og:image — instead of
   *  re-deriving a cover from `files`. Empty/absent means "no cover exists". */
  constructedCover?: string;
}

export interface CreatorArtworkImage extends CreatorArtworkBase {
  type: typeof CreatorArtworkImageTargetType;
}

export interface CreatorArtworkWorld extends CreatorArtworkBase {
  type: typeof CreatorArtworkWorldTargetType;
  sceneConfig: CreatorSceneConfig; // required parsed scene graph
}

export type CreatorArtwork = CreatorArtworkImage | CreatorArtworkWorld;

export interface CreatorCollection {
  id: string;
  type: typeof CreatorArtworkCollectionType;
  name?: string;
  description?: string;
  files?: CreatorFile[]; // includes optional cover assets
  artworks: CreatorArtwork[];
  /** Server-resolved cover image URL — see {@link CreatorArtworkBase.constructedCover}.
   *  For collections: own files first, then the first artwork with an image. */
  constructedCover?: string;
}

//------------------------------------------------------------ payload.
export type CreatorPayload = CreatorArtworkImage | CreatorArtworkWorld | CreatorCollection;

export const isCreatorArtworkImage = (
  payload: CreatorPayload
): payload is CreatorArtworkImage => payload.type === CreatorArtworkImageTargetType;

export const isCreatorArtworkWorld = (
  payload: CreatorPayload
): payload is CreatorArtworkWorld => payload.type === CreatorArtworkWorldTargetType;

export const isCreatorCollection = (
  payload: CreatorPayload
): payload is CreatorCollection => payload.type === CreatorArtworkCollectionType;
